import { forwardRef } from 'react'
import '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas.css'
import './BalancoPdfCanvas.css'

const COMPANY_NAME = 'Pró Médico Ltda'
const COMPANY_FOOTER = 'Pró Médico Ltda — Material Médico-Hospitalar | Pelotas/RS'

function formatMoney(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0)
}

function formatAsOf(isoDate) {
  if (!isoDate) return ''
  const [year, month, day] = String(isoDate).slice(0, 10).split('-')
  if (!year || !month || !day) return isoDate
  return `${day}/${month}/${year}`
}

function PdfRow({ name, amount, note, kind, indent }) {
  return (
    <tr className={`balanco-pdf-row${kind ? ` is-${kind}` : ''}`}>
      <td>
        <p className={`ar-pdf-desc-main${indent ? ' is-indent' : ''}`}>{name}</p>
        {note ? <p className="ar-pdf-desc-sub">{note}</p> : null}
      </td>
      <td className="right">{formatMoney(amount)}</td>
    </tr>
  )
}

function Section({ title, total, children }) {
  return (
    <section className="balanco-pdf-section">
      <div className="balanco-pdf-section-head">
        <h3>{title}</h3>
        <strong>{formatMoney(total)}</strong>
      </div>
      <table className="ar-pdf-table">
        <thead>
          <tr>
            <th>Conta</th>
            <th className="right">Valor</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </section>
  )
}

const BalancoPdfCanvas = forwardRef(function BalancoPdfCanvas({ sheet }, ref) {
  if (!sheet) return null

  const assets = sheet.assets
  const liabilities = sheet.liabilities
  const equity = sheet.equity
  const asOf = formatAsOf(sheet.asOf)
  const difference = Number(sheet.equationDifference) || 0

  return (
    <div className="ar-pdf-offscreen" aria-hidden="true">
      <div ref={ref} className="ar-pdf-canvas balanco-pdf">
        <header className="ar-pdf-header">
          <div className="ar-pdf-brand">
            <div className="ar-pdf-logo">PM</div>
            <div>
              <p className="ar-pdf-company-name">{COMPANY_NAME}</p>
              <p className="ar-pdf-company-sub">Material médico-hospitalar</p>
            </div>
          </div>
          <div className="ar-pdf-title-block">
            <h2 className="ar-pdf-title">Balanço contábil</h2>
            <p className="ar-pdf-meta">Posição em {asOf}</p>
          </div>
        </header>

        <div className="ar-pdf-body">
          <p className="balanco-pdf-note">
            Posição patrimonial na data. Caixa, estoque e títulos em aberto entram pelo saldo de hoje.
            Não acumula os últimos 12 meses e não fecha o exercício do ano.
          </p>

          <div className="balanco-pdf-summary">
            <article>
              <span>Ativo</span>
              <strong>{formatMoney(assets.total)}</strong>
            </article>
            <article>
              <span>Passivo</span>
              <strong>{formatMoney(liabilities.total)}</strong>
            </article>
            <article>
              <span>Patrimônio líquido</span>
              <strong>{formatMoney(equity.total)}</strong>
            </article>
          </div>

          <Section title="Ativo circulante" total={assets.current.total}>
            <PdfRow name="Caixa e equivalentes" amount={assets.current.cash.total} note="Calculado" kind="parent" />
            {assets.current.cash.accounts.map((account) => (
              <PdfRow
                key={account.id}
                name={account.name}
                amount={account.balance}
                indent
              />
            ))}
            <PdfRow name="Contas a receber" amount={assets.current.receivables.total} note="Calculado" />
            <PdfRow name="Estoques" amount={assets.current.inventory.total} note="Calculado" />
          </Section>

          <Section title="Ativo não circulante" total={assets.nonCurrent.total}>
            {assets.nonCurrent.items.length === 0 ? (
              <PdfRow name="Nenhum item cadastrado" amount={0} />
            ) : assets.nonCurrent.items.map((item) => (
              <PdfRow key={item.id} name={item.name} amount={item.amount} note={item.notes} />
            ))}
          </Section>

          <Section title="Passivo" total={liabilities.total}>
            <PdfRow name="Contas a pagar" amount={liabilities.payables.total} note="Calculado" />
            {liabilities.items.map((item) => (
              <PdfRow key={item.id} name={item.name} amount={item.amount} note={item.notes} />
            ))}
          </Section>

          <Section title="Patrimônio líquido" total={equity.total}>
            {equity.items.map((item) => (
              <PdfRow key={item.id} name={item.name} amount={item.amount} note={item.notes} />
            ))}
            <PdfRow
              name="Lucros ou prejuízos acumulados"
              amount={equity.retainedEarnings}
              note="Calculado"
              kind="parent"
            />
          </Section>

          <div className="ar-pdf-summary">
            <div className="ar-pdf-total-box">
              <div className="ar-pdf-total-row">
                <span>Ativo</span>
                <strong>{formatMoney(assets.total)}</strong>
              </div>
              <div className="ar-pdf-total-row">
                <span>Passivo + patrimônio líquido</span>
                <strong>{formatMoney(liabilities.total + equity.total)}</strong>
              </div>
              <div className="ar-pdf-total-final">
                <p className="ar-pdf-total-final-label">
                  {Math.abs(difference) < 0.01 ? 'Equação fechada' : 'Diferença'}
                </p>
                <p className="ar-pdf-total-final-value">
                  {Math.abs(difference) < 0.01 ? 'Ativo = Passivo + PL' : formatMoney(difference)}
                </p>
              </div>
            </div>
          </div>

          <footer className="ar-pdf-footer balanco-pdf-footer">
            {COMPANY_FOOTER} · Emitido em {asOf} · Documento gerencial
          </footer>
        </div>
      </div>
    </div>
  )
})

export default BalancoPdfCanvas
